import { action, observable, runInAction } from 'mobx'
import { message } from 'antd'
import { cloneDeep } from 'lodash'
import { StoreExt } from '@utils/reactExt'

import {
  IAssets,
  ICashHistory,
  IHeartHistory,
  IItemHistory,
  IMoneyHistory,
  Schema$RecordUserAsset,
  Schema$RecordUserMoney,
} from '@protocol/bank'
import { PROPERTY_ID } from 'adv-protocol/src/GameCommon'

export type Assets = IAssets.Schema
export type MoneyHistory = IMoneyHistory.Schema
export type CashHistory = ICashHistory.Schema
export type HeartHistory = IHeartHistory.Schema
export type ItemHistory = IItemHistory.Schema

export interface AssetsRecordSource {
  propertyId?: PROPERTY_ID
  tracks?: Schema$RecordUserAsset[] | Schema$RecordUserMoney[]
  lastFetched?: Schema$RecordUserAsset[] | Schema$RecordUserMoney[]
  hasMore?: boolean
  limit?: number
  total?: number
}

export class BankStore extends StoreExt {

  oldMyBankPropertyId: PROPERTY_ID

  @observable
  historyList: AssetsRecordSource

  @observable
  diffDate: Date

  @observable
  isLoading = false

  @observable
  hasMore = true

  @observable
  currentMyBankTabIndex = 0

  @observable
  currentMyBankPropertyId: PROPERTY_ID

  @action
  setDiffDate = (date: Date): Date => {
    this.diffDate = date
    return date
  }

  @action
  reset = () => {
    this.historyList = { tracks: [] }
    this.diffDate = null
    this.isLoading = false
    this.hasMore = true
  }

  @action
  fetchHistoryList = async (amount: number, resetForce = false): Promise<void> => {
    const reset = resetForce === true || this.oldMyBankPropertyId !== this.currentMyBankPropertyId
    if (reset) {
      this.reset()
    }
    if (this.isLoading || !this.hasMore) {
      return
    }
    try {
      this.isLoading = true
      const targetLength = this.historyList ? this.historyList.tracks.length : 0
      const offset = reset ? 0 : targetLength
      const { limit, total, items } = await this.api.bank.getHistoryList({
        offset,
        limit: amount,
      }, this.currentMyBankPropertyId)
      this.oldMyBankPropertyId = this.currentMyBankPropertyId

      this.updateTracks({ limit, total, items })
    } catch (err) {
      console.error(err)
      message.error('뱅크 내역 구성중 오류가 발생 했습니다!')
    }
  }

  @action
  updateTracks = (fetchData: MoneyHistory | CashHistory | HeartHistory | ItemHistory): AssetsRecordSource => {
    const { limit, total, items } = fetchData
    const { tracks = [] } = this.historyList
    const newTracks: Schema$RecordUserAsset[] | Schema$RecordUserMoney[] = cloneDeep(tracks)
    const lastFetched: Schema$RecordUserAsset[] | Schema$RecordUserMoney[] = []

    for (const el of items) {
      newTracks.push(el)
      lastFetched.push(el)
    }

    const hasMore = newTracks.length < total
    const newSource = {
      propertyId: this.currentMyBankPropertyId,
      tracks: newTracks,
      lastFetched,
      hasMore,
      limit,
      total,
    }

    runInAction(() => {
      this.historyList = newSource
      this.hasMore = hasMore
      this.isLoading = false
    })

    return newSource
  }

  @action
  setSelectedMyBankTab = (tabIndex: number, propertyId: PROPERTY_ID): void => {
    this.currentMyBankTabIndex = tabIndex
    this.currentMyBankPropertyId = propertyId
  }
}

export default new BankStore()
